import {
  call,
  put,
  takeLatest,
  select,
  debounce,
  all,
} from 'redux-saga/effects';
import { selectUserCompanyId } from 'modules/loggedUserInfo/selectors';
import { UPLOAD_DOCUMENT_TRANSLATIONS } from 'const/translations';
import { downloadFile } from 'helpers/common';
import { createFileWithCorrectType } from 'shared/lib';
import {
  GET_UPLOAD_HISTORY_REQUEST,
  getUploadHistorySuccess,
  getUploadHistoryError,
  UPLOAD_FILES_REQUEST,
  uploadFilesSuccess,
  uploadFilesError,
  moreUploadHistorySuccess,
  moreUploadHistoryError,
  MORE_UPLOAD_HISTORY_REQUEST,
  DOWNLOAD_DOCUMENT_REQUEST,
  downloadDocumentError,
  downloadDocumentSuccess,
  GET_UPLOADERS_HISTORY_REQUEST,
  getUploadersHistorySuccess,
  getUploadHistoryRequestStart,
  getUploadHistoryRequest,
  MARK_DOCUMENT_AS_MISTAKEN,
  markDocumentAsMistakenActionSuccess,
} from '../actions';
import {
  selectUploadedHistoryLength,
  selectUploadHistoryIds,
  selectUploadHistoryMeta,
} from '../selectors';
import {
  getUploadHistory,
  uploadDocuments,
  downloadDocument,
  getUploadersHistory,
  markDocumentAsMistakenRequest,
} from '../services';
import { notification } from 'antd';

/**
 * fetch history of uploaded files
 */
function* uploadHistoryWorker({ payload: { companyId, ...rest } }) {
  try {
    yield put(getUploadHistoryRequestStart());
    const {
      data: { documents },
      meta,
    } = yield call(getUploadHistory, { companyId, ...rest });
    yield put(getUploadHistorySuccess({ documents, meta }));
  } catch (error) {
    yield put(getUploadHistoryError({ error }));
  }
}

/**
 * Fetch next chunk of the same upload history
 */
function* uploadMoreHistoryWorker({ payload: { fileName, range, userName } }) {
  try {
    const { limit } = yield select(selectUploadHistoryMeta);
    const downloadedAmount = yield select(selectUploadedHistoryLength);
    const companyId = yield select(selectUserCompanyId);

    const [dateFrom, dateTo] = range || [];

    const {
      data: { documents },
      meta,
    } = yield call(getUploadHistory, {
      limit,
      offset: downloadedAmount,
      companyId,
      clientId: userName,
      name: fileName || undefined,
      dateFrom: dateFrom?.format('DD/MM/YYYY'),
      dateTo: dateTo?.format('DD/MM/YYYY'),
    });
    const docsIds = yield select(selectUploadHistoryIds);
    yield put(
      moreUploadHistorySuccess({
        documents: documents.filter(({ id }) => !docsIds.includes(id)),
        meta,
      })
    );
  } catch (error) {
    yield put(moreUploadHistoryError({ error }));
  }
}

/**
 * upload files to backend
 */
function* uploadFilesWorker({ payload: { uploadedFiles, onError, type } }) {
  const companyId = yield select(selectUserCompanyId);

  try {
    const formData = new FormData();

    uploadedFiles.forEach(({ originFileObj: file, name }) => {
      const fileWithCorrectType = createFileWithCorrectType(file, name);
      formData.append('files', fileWithCorrectType);
    });

    formData.append('companyId', companyId);
    formData.append('type', type);

    const {
      data: { documents },
    } = yield call(uploadDocuments, formData);
    yield put(uploadFilesSuccess({ documents }));
  } catch (error) {
    yield put(uploadFilesError({ error }));
    onError(
      error.error || UPLOAD_DOCUMENT_TRANSLATIONS.MESSAGES.ERROR_DURING_UPLOAD
    );

    yield put(getUploadHistoryRequest({ companyId }));
  }
}

function* downloadFileWorker({ payload: { fileId, fileName } }) {
  try {
    const { fileData } = yield call(downloadDocument, fileId);

    yield call(downloadFile, fileData, fileName);
    yield put(downloadDocumentSuccess({ fileId }));
  } catch (error) {
    console.error(error);
    yield put(downloadDocumentError({ fileId }));
  }
}

function* uploadersHistoryWorker({ payload: { companyId } }) {
  try {
    const {
      data: { clients },
    } = yield call(getUploadersHistory, {
      companyId,
    });

    yield put(getUploadersHistorySuccess(clients));
  } catch (error) {
    console.warn(error);
  }
}

function* debounceUploadHistoryWorker() {
  yield debounce(400, GET_UPLOAD_HISTORY_REQUEST, uploadHistoryWorker);
}

function* markDocumentsAsMistakenWorker({ payload: { documentId } }) {
  try {
    const {
      data: { document },
    } = yield call(markDocumentAsMistakenRequest, documentId);

    yield put(markDocumentAsMistakenActionSuccess({ document }));
  } catch (error) {
    console.warn(error);
    notification.error({ message: error.error });
  }
}

export function* uploadDocumentsMainSaga() {
  yield all([
    takeLatest(UPLOAD_FILES_REQUEST, uploadFilesWorker),
    takeLatest(MORE_UPLOAD_HISTORY_REQUEST, uploadMoreHistoryWorker),
    takeLatest(DOWNLOAD_DOCUMENT_REQUEST, downloadFileWorker),
    takeLatest(GET_UPLOADERS_HISTORY_REQUEST, uploadersHistoryWorker),
    takeLatest(MARK_DOCUMENT_AS_MISTAKEN, markDocumentsAsMistakenWorker),
    debounceUploadHistoryWorker(),
  ]);
}
